import React, { useState } from "react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Share2, Link2, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { SITE_URL } from "@/lib/api";

/**
 * Botón discreto que abre un popover con opciones de compartir.
 *
 * Props:
 *  - title: Título corto (ej: "Adicción", "Inventario")
 *  - text:  Cuerpo / definición / verso
 *  - path:  Path con ancla (ej: "/glosario#term-adiccion")
 *  - label: Texto del botón ("Compartir" por defecto)
 *  - variant: "icon" (solo ícono, hover en card) | "inline" (chip pequeño)
 *  - testid: data-testid prefix
 *  - source: Etiqueta de origen (ej: "Glosario del Adicto")
 *  - titlePosition: "start" (default, "Título: texto") | "end" (texto + — Título —, golpe final)
 *  - hashtags: array de hashtags sin "#" (default: ["nitanadicto"])
 */
export default function ShareMenu({
    title,
    text,
    path,
    label = "Compartir",
    variant = "icon",
    testid = "share",
    source = "Autodestrucción S.A.",
    titlePosition = "start",
    hashtags = ["nitanadicto"],
}) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const url = `${SITE_URL}${path}`;
    const hashtagString = hashtags.map((h) => `#${h}`).join(" ");

    // Texto formateado largo (para Copy Link)
    const formatted =
        titlePosition === "end"
            ? `"${text}"\n— ${title} —\n\n@NiTanAdicto · ${source} · ${hashtagString}\n${url}`
            : `"${text}"\n— @NiTanAdicto · ${source} · ${hashtagString}\n${url}`;

    // Texto corto para WhatsApp / Telegram (sin hashtags duplicados con Twitter native param)
    const shortText =
        titlePosition === "end"
            ? `"${text}" — ${title} — @NiTanAdicto ${hashtagString}`
            : `"${title}: ${text}" — @NiTanAdicto ${hashtagString}`;

    // Texto Twitter (sin hashtags inline, Twitter los recibe por param dedicado)
    const twitterText =
        titlePosition === "end"
            ? `"${text}" — ${title} — @NiTanAdicto`
            : `"${title}: ${text}" — @NiTanAdicto`;

    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(shortText);
    const encodedTwitterText = encodeURIComponent(twitterText);
    const encodedHashtags = encodeURIComponent(hashtags.join(","));

    const targets = [
        {
            id: "twitter",
            label: "Twitter / X",
            href: `https://twitter.com/intent/tweet?text=${encodedTwitterText}&url=${encodedUrl}&hashtags=${encodedHashtags}`,
            icon: TwitterIcon,
        },
        {
            id: "facebook",
            label: "Facebook",
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            icon: FacebookIcon,
        },
        {
            id: "whatsapp",
            label: "WhatsApp",
            href: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
            icon: WhatsappIcon,
        },
        {
            id: "telegram",
            label: "Telegram",
            href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
            icon: TelegramIcon,
        },
    ];

    async function nativeShare() {
        if (navigator.share) {
            try {
                await navigator.share({
                    title,
                    text: `${shortText}`,
                    url,
                });
                setOpen(false);
            } catch {
                /* user dismissed */
            }
        }
    }

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(formatted);
            setCopied(true);
            toast.success("Copiado al portapapeles");
            setTimeout(() => {
                setCopied(false);
                setOpen(false);
            }, 1200);
        } catch {
            toast.error("No se pudo copiar.");
        }
    }

    const hasNativeShare =
        typeof navigator !== "undefined" && !!navigator.share;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    onClick={(e) => e.stopPropagation()}
                    className={
                        variant === "inline"
                            ? "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)] hover:text-[var(--danger)] transition-colors"
                            : "inline-flex items-center justify-center w-8 h-8 border border-[var(--line)] text-[var(--ink-muted)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors"
                    }
                    aria-label={`Compartir: ${title}`}
                    data-testid={`${testid}-trigger`}
                >
                    <Share2 className="w-3.5 h-3.5" />
                    {variant === "inline" && <span>{label}</span>}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                className="w-64 p-0 bg-[#0d0d0d] border border-[var(--danger)] rounded-none shadow-[6px_6px_0_rgba(211,47,47,0.4)]"
                data-testid={`${testid}-popover`}
            >
                <div className="border-b border-[var(--line)] bg-[var(--paper-2)] px-4 py-2 flex justify-between items-center">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-black font-bold">
                        Compartir
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)]">
                        S.A.
                    </span>
                </div>

                <div className="p-2 space-y-1">
                    {hasNativeShare && (
                        <button
                            onClick={nativeShare}
                            className="w-full flex items-center gap-3 px-3 py-2.5 font-mono text-xs text-[var(--paper-2)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)] transition-colors"
                            data-testid={`${testid}-native`}
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span>Compartir…</span>
                        </button>
                    )}

                    <button
                        onClick={copyLink}
                        className="w-full flex items-center gap-3 px-3 py-2.5 font-mono text-xs text-[var(--paper-2)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)] transition-colors"
                        data-testid={`${testid}-copy`}
                    >
                        {copied ? (
                            <Check className="w-3.5 h-3.5 text-[var(--warning)]" />
                        ) : (
                            <Link2 className="w-3.5 h-3.5" />
                        )}
                        <span>{copied ? "Copiado" : "Copiar enlace"}</span>
                    </button>

                    <div className="h-px bg-[var(--line)] my-1" />

                    {targets.map((t) => (
                        <a
                            key={t.id}
                            href={t.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 font-mono text-xs text-[var(--paper-2)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)] transition-colors"
                            data-testid={`${testid}-${t.id}`}
                        >
                            <t.icon className="w-3.5 h-3.5" />
                            <span>{t.label}</span>
                        </a>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// Brand icons (simple, monoline SVGs to match design language)
function TwitterIcon({ className }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden
        >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

function FacebookIcon({ className }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden
        >
            <path d="M22 12.07C22 6.51 17.52 2 12 2S2 6.51 2 12.07c0 5.03 3.66 9.2 8.44 9.93v-7.02H7.9V12.07h2.54V9.83c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.9h2.78l-.44 2.91h-2.34v7.02C18.34 21.27 22 17.1 22 12.07z" />
        </svg>
    );
}

function WhatsappIcon({ className }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden
        >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 2C6.582 2 2.14 6.44 2.14 11.9c0 1.768.464 3.493 1.345 5.012L2 22l5.21-1.452c1.467.796 3.12 1.213 4.83 1.213h.004c5.46 0 9.901-4.44 9.901-9.9 0-2.647-1.03-5.135-2.901-7.005C17.175 3.031 14.687 2 12.04 2z" />
        </svg>
    );
}

function TelegramIcon({ className }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden
        >
            <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
        </svg>
    );
}
